import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
    changeMpin,
    getMpinStatus,
    setupMpin,
    verifyMpin,
} from '../../store/actions/authActionCreator';
import {
    getLoginUser,
    getUserToken,
    logout,
    showMessage,
} from '../../utils/helper';
import {
    clearMpinContext,
    contextFromStatus,
    enterApp,
    isMpinPending,
    MpinContext,
    readMpinContext,
    writeMpinContext,
} from '../../utils/mpinGate';
import {
    CONFIRM_MISMATCH_MESSAGE,
    MPIN_VARIANTS,
    MpinErrors,
    MpinSubmitOutcome,
    MpinValues,
    normalizeMpinOutcome,
} from './mpin.helper';

/**
 * All state and behavior for the /mpin gate page. One generic submit for
 * the three server-driven variants; the view renders from the variant
 * config table only.
 *
 * SECURITY: raw MPIN values live exclusively in this hook's `values`
 * state. They are never persisted, dispatched into redux, or logged.
 */
const useMpin = (history: any) => {
    const dispatch = useDispatch();

    const [context, setContext] = useState<MpinContext | null>(() =>
        readMpinContext(),
    );
    // True while the mount-time GET /user/mpin/status refresh is in flight.
    // The seeded variant renders underneath — no blank screen.
    const [resolving, setResolving] = useState(true);
    const [values, setValues] = useState<MpinValues>({});
    const [errors, setErrors] = useState<MpinErrors>({});
    const [submitting, setSubmitting] = useState(false);
    // Shown only after a failed attempt in THIS session — never seeded from
    // the stored/status counters, so a fresh screen shows no attempt count.
    const [attemptsRemaining, setAttemptsRemaining] = useState<number | null>(
        null,
    );
    const [blockedUntilMs, setBlockedUntilMs] = useState<number | null>(() => {
        const stored = readMpinContext()?.blockedUntilMs ?? null;
        return stored && stored > Date.now() ? stored : null;
    });
    const [secondsLeft, setSecondsLeft] = useState(0);
    const [successTitle, setSuccessTitle] = useState<string | null>(null);
    const [forgotOpen, setForgotOpen] = useState(false);
    // Back-exit warning (browser/hardware/keyboard back). The POP itself is
    // always blocked; this only controls the confirmation dialog.
    const [exitConfirmOpen, setExitConfirmOpen] = useState(false);

    // Entry into the app must run exactly once (success tick timer +
    // status callbacks can race a fast re-render).
    const enteredRef = useRef(false);
    const enterAppOnce = useCallback(() => {
        if (enteredRef.current) return;
        enteredRef.current = true;
        enterApp(dispatch, history);
    }, [dispatch, history]);

    const applyContext = useCallback((next: MpinContext) => {
        writeMpinContext(next);
        setContext(next);
        setBlockedUntilMs(
            next.blockedUntilMs && next.blockedUntilMs > Date.now()
                ? next.blockedUntilMs
                : null,
        );
    }, []);

    // Mount: guard, then re-resolve against the server (design decision 4 —
    // /status is authoritative for state and lockout counters on every
    // entry: login push, reload resume, SSO, direct URL).
    useEffect(() => {
        if (!getUserToken()) {
            history.replace('/login');
            return;
        }
        dispatch(
            getMpinStatus(
                (envelope: any) => {
                    const fresh = contextFromStatus(envelope?.response);
                    if (fresh) {
                        applyContext(fresh);
                    } else {
                        // NOT_APPLICABLE — nothing owed (covers an admin
                        // clearing the cohort, and direct /mpin visits by
                        // ungated users). Never a trap.
                        clearMpinContext();
                        enterAppOnce();
                        return;
                    }
                    setResolving(false);
                },
                () => {
                    // Status failed. Flag armed → fail CLOSED on the
                    // persisted variant (the server still gates every
                    // submit). No flag and no persisted context → this
                    // user has no evidence of a gate: proceed (fail open).
                    if (isMpinPending()) {
                        if (!readMpinContext()) {
                            // Corrupt stored context — can't render a
                            // variant; a fresh OTP login re-arms cleanly.
                            logout();
                            return;
                        }
                        setResolving(false);
                    } else {
                        enterAppOnce();
                    }
                },
            ),
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Back must never leave the gate silently — not into pre-gate history
    // (e.g. the settings page behind an admin-triggered change gate) and
    // not out of the site (fresh login lands here via history.replace, so
    // /mpin can be the tab's FIRST entry and back would exit to the
    // browser's new-tab page — a router block can't veto a cross-document
    // navigation). So: push a same-URL GUARD entry on mount; every back
    // press (browser button, keyboard back, mobile hardware back) then
    // pops the guard — a same-document popstate — where we re-arm the
    // guard and show the exit warning instead. Confirming logs out
    // (logout() clears local storage and lands on the login screen).
    useEffect(() => {
        window.history.pushState(null, '', window.location.href);
        const onPopState = () => {
            // Re-arm first so the user is already anchored back on the
            // gate, then ask.
            window.history.pushState(null, '', window.location.href);
            setExitConfirmOpen(true);
        };
        window.addEventListener('popstate', onPopState);
        return () => window.removeEventListener('popstate', onPopState);
    }, []);

    // Lockout countdown — ticks once a second; expiry forces a fresh OTP
    // login instead of re-enabling MPIN entry.
    useEffect(() => {
        if (!blockedUntilMs) {
            setSecondsLeft(0);
            return;
        }
        const tick = () => {
            const remaining = Math.ceil((blockedUntilMs - Date.now()) / 1000);
            if (remaining <= 0) {
                showMessage(
                    'Your PIN was locked temporarily. Please log in again with OTP.',
                    'info',
                );
                logout();
                return;
            }
            setSecondsLeft(remaining);
        };
        tick();
        const interval = setInterval(tick, 1000);
        return () => clearInterval(interval);
    }, [blockedUntilMs]);

    const variant =
        context && context.state !== 'NOT_APPLICABLE'
            ? MPIN_VARIANTS[context.state]
            : null;
    const mpinLength = context?.length ?? 6;
    const isBlocked = !!blockedUntilMs;

    const allFilled =
        !!variant &&
        variant.fields.every(
            (field) => (values[field.key] ?? '').length === mpinLength,
        );
    const canSubmit =
        allFilled && !submitting && !isBlocked && !successTitle && !!context;

    const handleFieldChange = (fieldKey: string) => (value: string) => {
        setValues((prev) => ({ ...prev, [fieldKey]: value }));
        // Stale-error rule: editing a field clears its error (and any
        // form-level one) so old messages never describe new input.
        setErrors((prev) => {
            if (!prev[fieldKey as keyof MpinErrors] && !prev.form) return prev;
            const next = { ...prev };
            delete next[fieldKey as keyof MpinErrors];
            delete next.form;
            return next;
        });
    };

    const handleOutcome = (outcome: MpinSubmitOutcome) => {
        if (outcome.ok) {
            clearMpinContext();
            const title = variant?.successTitle ?? null;
            if (title) {
                setSuccessTitle(title);
                setTimeout(enterAppOnce, 1000);
            } else {
                enterAppOnce();
            }
            return;
        }
        if (outcome.field) {
            setErrors({ [outcome.field]: outcome.message });
        }
        if (outcome.clearFields?.length) {
            setValues((prev) => {
                const next = { ...prev };
                outcome.clearFields?.forEach((fieldKey) => {
                    next[fieldKey] = '';
                });
                return next;
            });
        }
        if (outcome.attemptsRemaining !== undefined) {
            setAttemptsRemaining(outcome.attemptsRemaining ?? null);
        }
        if (outcome.blockedForSeconds && outcome.blockedForSeconds > 0) {
            const blockedUntil = Date.now() + outcome.blockedForSeconds * 1000;
            // Mirror into the persisted context so a reload mid-lockout
            // still shows the correct remaining time.
            if (context) {
                applyContext({
                    ...context,
                    // Keep the server's lockout copy so the panel (and a
                    // reload mid-lockout) can show it verbatim.
                    message: outcome.message ?? context.message,
                    attemptsRemaining: outcome.attemptsRemaining ?? 0,
                    blockedUntilMs: blockedUntil,
                });
            } else {
                setBlockedUntilMs(blockedUntil);
            }
            setErrors({});
        }
    };

    const handleSubmit = () => {
        if (!canSubmit || !context || !variant) return;
        const gatedState = context.state;
        if (gatedState === 'NOT_APPLICABLE') return; // type guard — never gated

        // Client-side pre-flight: the confirm row must match before any
        // API call — confirm clears, the source row is kept.
        const pair = variant.confirmPair;
        if (pair && values[pair.source] !== values[pair.confirm]) {
            setErrors({ [pair.confirm]: CONFIRM_MISMATCH_MESSAGE });
            setValues((prev) => ({ ...prev, [pair.confirm]: '' }));
            return;
        }

        setSubmitting(true);
        const done = (envelope: any) => {
            setSubmitting(false);
            handleOutcome(normalizeMpinOutcome(gatedState, envelope));
        };

        if (gatedState === 'SETUP_REQUIRED') {
            dispatch(
                setupMpin(
                    {
                        mpin: values.pin ?? '',
                        confirmMpin: values.confirmPin ?? '',
                    },
                    done,
                    done,
                ),
            );
        } else if (gatedState === 'VERIFY_REQUIRED') {
            dispatch(verifyMpin({ mpin: values.pin ?? '' }, done, done));
        } else {
            dispatch(
                changeMpin(
                    {
                        oldMpin: values.oldPin ?? '',
                        newMpin: values.newPin ?? '',
                        confirmMpin: values.confirmNewPin ?? '',
                    },
                    done,
                    done,
                ),
            );
        }
    };

    return {
        variant,
        context,
        mpinLength,
        resolving,
        values,
        errors,
        submitting,
        attemptsRemaining,
        isBlocked,
        secondsLeft,
        successTitle,
        forgotOpen,
        exitConfirmOpen,
        canSubmit,
        userName: getLoginUser()?.name ?? '',
        handleFieldChange,
        handleSubmit,
        openForgot: () => setForgotOpen(true),
        closeForgot: () => setForgotOpen(false),
        confirmExit: () => logout(),
        cancelExit: () => setExitConfirmOpen(false),
        handleLogout: () => logout(),
    };
};

export default useMpin;
